import { ApplicationConfig, provideZoneChangeDetection, isDevMode, LOCALE_ID, inject, provideAppInitializer } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePl from '@angular/common/locales/pl';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, withPreloading } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NetworkAwarePreloadingStrategy } from './shared/strategies/network-aware-preloading.strategy';

import { routes } from './app.routes';

import { MessageService } from 'primeng/api';
// import { authInterceptor } from './interceptors/auth.interceptor';
// import { ErrorService } from './shared/services';
// import { HttpErrorInterceptor } from './interceptors/http-error-hanlder.interceptor';
import { providePrimeNG } from 'primeng/config';
import MyPreset from '../theme/app-theme';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { TokenRefreshInterceptor } from './interceptors/token-refresh.interceptor';
import { DevApiKeyInterceptor } from './interceptors/dev-api-key.interceptor';
import { DeviceHeaderInterceptor } from './interceptors/device-header.interceptor';
import { ApiTimeoutInterceptor } from './interceptors/api-timeout.interceptor';
import { provideMapPicker } from '@konradkrk/ngx-map-picker';
import { providePropertyWizard } from './modules/object/modules/property-wizard';
import { environment } from '../environments/environment';
import { RequestBoardAccessService } from './modules/request-board/services/request-board-access.service';


// Dane locale pl muszą być zarejestrowane, inaczej DatePipe renderuje po
// angielsku ("2 Aug", "August") mimo polskiego UI.
registerLocaleData(localePl, 'pl');

/**
 * LOCALE_ID dobierany tak samo jak język w LanguageService (localStorage → pl).
 * Zmiana języka w trakcie sesji odświeży DatePipe dopiero po przeładowaniu —
 * teksty tłumaczy Transloco na bieżąco.
 */
function resolveLocaleId(): string {
  const lang = localStorage.getItem('userLanguage') || 'pl';
  return ['pl', 'en'].includes(lang) ? lang : 'pl';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: LOCALE_ID, useFactory: resolveLocaleId },
    // Lazy-chunki zakładek dociągają się w tle po starcie — bez tego pierwszy
    // klik w każdą zakładkę czekał na fetch chunka (na mobile = wielosekundowy
    // "freeze" bez feedbacku). Strategia świadoma sieci pomija/odracza preload
    // na wolnym/oszczędnym łączu, by nie ścigać się z fetchem danych dashboardu.
    provideRouter(routes, withPreloading(NetworkAwarePreloadingStrategy)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideTransloco({
      config: {
        availableLangs: ['en', 'pl'],
        defaultLang: 'pl',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: '.app-dark' // Klasa CSS do przełączania trybu
        }
      },
      overlayOptions: {
        appendTo: 'body'
      },
    }),
    provideMapPicker({
      defaultCenter: { lat: 52.2297, lng: 21.0122 },
      defaultZoom: 13,
    }),
    providePropertyWizard({
      apiUrl: environment.apiUrl,
      // Upload zdjęć korzysta z tokenowego endpointu na KAŻDYM środowisku
      // (token leci przez normalny auth interceptorów). Wariant testowy:
      // { tokenEndpoint: 'files/upload-token/test', testToken: '...' }.
      fileUpload: { tokenEndpoint: 'files/upload-token' },
    }),
    { provide: HTTP_INTERCEPTORS, useClass: DevApiKeyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DeviceHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenRefreshInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    // OSTATNI w łańcuchu (najbliżej sieci): mierzy czas samego requestu HTTP,
    // a nie oczekiwania w kolejce refresh-tokena; retry ponawia tylko strzał sieciowy.
    { provide: HTTP_INTERCEPTORS, useClass: ApiTimeoutInterceptor, multi: true },
    MessageService,
    // Scalone uprawnienia (per-user ∪ rola org) z GET /auth/permissions — bez blokowania startu;
    // potrzebne do gatingu „Zaproponuj”/„Wycofaj” (request_board.propose) przy sesji z localStorage.
    provideAppInitializer(() => inject(RequestBoardAccessService).refreshOnStartup()),
  ],
};
